import React from 'react';
import Appbase from 'appbase-js';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { withStyles, Grid, CircularProgress } from '@material-ui/core';
import {
	ResponsiveContainer,
	XAxis,
	Bar,
	YAxis,
	Area,
	CartesianGrid,
	ComposedChart,
} from 'recharts';
import { feature } from 'topojson-client';
import { ComposableMap, ZoomableGroup, Geographies, Geography } from 'react-simple-maps';
import ReactTooltip from 'react-tooltip';

import { APP, CRED, URL, TYPE } from '../constants';
import StyledCard from '../components/StyledCard';
import { northAmericaCountries, europeanCountries, getContinent, getCountryColor } from '../utils';
import Barchart from '../charts/recharts/Barchart';
import Linechart from '../charts/recharts/Linechart';
import Areachart from '../charts/recharts/Areachart';
import Radarchart from '../charts/recharts/Radarchart';
import Piechart from '../charts/recharts/Piechart';
import Navigation from '../components/Navigation';

const styles = theme => ({
	root: {
		border: `1px solid ${theme.palette.grey['200']}`,
		borderRadius: '2px',
	},
	spinner: {
		position: 'absolute',
		top: '50%',
		left: '50%',
	},
	notFound: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexDirection: 'column',
	},
	detail: {
		'@media(max-width: 600px)': {
			fontSize: '15px',
			fontWeight: '500',
		},
	},
});

class Game extends React.Component {
	constructor() {
		super();
		this.state = {
			isLoading: true,
			game: null,
			geographyPaths: [],
		};
		this.appbaseRef = Appbase({
			url: URL,
			app: APP,
			credentials: CRED,
		});
	}

	componentDidMount() {
		this.loadGame();
	}

	componentDidUpdate(prevProps) {
		const {
			location: { pathname },
		} = prevProps;
		const {
			location: { pathname: newPath },
		} = this.props;

		if (newPath !== pathname) {
			this.loadGame();
		}
	}

	loadGame = async () => {
		const {
			match: {
				params: { id },
			},
		} = this.props;
		this.setState({
			isLoading: true,
			game: null,
		});
		const gameDetails = await this.appbaseRef.get({
			type: TYPE,
			id,
		});

		if (gameDetails.found) {
			this.setState({
				game: { ...gameDetails._source },
				isLoading: false,
			});
			this.loadPaths();
		} else {
			this.setState({
				isLoading: false,
			});
		}
	};

	getSales = ({ properties: { name } }) => {
		const { game } = this.state;
		if (northAmericaCountries.includes(name)) {
			return game.NA_Sales;
		}
		if (Object.values(europeanCountries).includes(name)) {
			return game.EU_Sales;
		}
		if (name === 'Japan') {
			return game.JP_Sales;
		}
		return game.Other_Sales;
	};

	loadPaths = () => {
		fetch(
			'https://raw.githubusercontent.com/zcreativelabs/react-simple-maps/master/topojson-maps/world-50m.json',
		)
			.then(res => res.json())
			.then(res => {
				const world = res;
				const countries = feature(world, world.objects[Object.keys(world.objects)[0]])
					.features;
				const results = countries.map(country => ({
					...country,
					properties: {
						sales: this.getSales(country),
						...country.properties,
					},
				}));
				this.setState(
					{
						geographyPaths: results,
					},
					() =>
						setTimeout(() => {
							ReactTooltip.rebuild();
						}, 100),
				);
			});
	};

	render() {
		const { isLoading, game, geographyPaths } = this.state;
		const { history, classes } = this.props;

		if (isLoading) {
			return (
				<div className={classes.spinner}>
					<CircularProgress />
				</div>
			);
		}

		let salesData = [];
		let gameDetails = [];

		if (game) {
			salesData = [
				{
					name: 'Europe',
					sales: game.EU_Sales,
				},
				{
					name: 'Global',
					sales: game.Global_Sales,
				},
				{
					name: 'North America',
					sales: game.NA_Sales,
				},
				{
					name: 'Japan',
					sales: game.JP_Sales,
				},
				{
					name: 'Other',
					sales: game.Other_Sales,
				},
			];

			gameDetails = [
				{
					name: 'Name',
					value: game.Name,
				},
				{
					name: 'Publisher',
					value: game.Publisher,
				},
				{
					name: 'Release Year',
					value: game.Year_of_Release,
				},
				{
					name: 'Genre',
					value: game.Genre,
				},
				{
					name: 'Rating',
					value: game.Rating,
				},
				{
					name: 'Platform',
					value: game.Platform,
				},
			];
		}
		return (
			<React.Fragment>
				{game && (
					<Navigation
						links={[
							{
								to: '/',
								title: 'Home',
							},
							{
								to: '/platform',
								title: 'Platforms',
							},
							{
								to: `/platform/?platform="${game.Platform}"`,
								title: game.Platform,
							},
						]}
						currentLink={game.Name}
					/>
				)}
				{game ? (
					<Grid container spacing={4}>
						<StyledCard sizes={{ xs: 12 }}>
							<Grid container spacing={4}>
								{gameDetails.map(item => (
									<StyledCard
										noBorder
										title={item.name}
										sizes={{
											md: 4,
											sm: 6,
											xs: 6,
										}}
										key={item.name}
									>
										<Typography
											component="h6"
											variant="h6"
											color="textPrimary"
											className={classes.detail}
										>
											{item.value}
										</Typography>
									</StyledCard>
								))}
							</Grid>
						</StyledCard>

						<StyledCard title="Sales on Map" sizes={{ sm: 6, xs: 12 }}>
							<ComposableMap
								projectionConfig={{
									scale: 205,
									rotation: [-11, 0, 50],
								}}
								width={980}
								height={551}
								style={{
									width: '100%',
									height: 'auto',
								}}
							>
								<ZoomableGroup center={[0, 0]}>
									<Geographies geography={geographyPaths}>
										{({ geographies }) => {
											return geographies.map(geography => {
												return (
													<Geography
														key={geography.properties.NAME}
														data-tip={`${getContinent(
															geography.properties.NAME,
														)} ${
															geography.properties.sales
														} million sales`}
														geography={geography}
														// projection={projection}
														style={{
															default: {
																fill: getCountryColor(
																	geography.properties.NAME,
																),
																stroke: '#607D8B',
																strokeWidth: 0.75,
																outline: 'none',
															},
															hover: {
																fill: '#F0F2F5',
																stroke: '#607D8B',
																strokeWidth: 0.75,
																outline: 'none',
															},
															pressed: {
																fill: '#F0F2F5',
																stroke: '#607D8B',
																strokeWidth: 0.75,
																outline: 'none',
															},
														}}
													/>
												);
											});
										}}
									</Geographies>
								</ZoomableGroup>
							</ComposableMap>
							<ReactTooltip />
						</StyledCard>

						<StyledCard title="Sales on Composed Chart" sizes={{ sm: 6, xs: 12 }}>
							<ResponsiveContainer width="100%" height="80%" aspect={4.0 / 2.3}>
								<ComposedChart
									data={salesData}
									height={250}
									margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
								>
									<XAxis dataKey="name" />
									<YAxis />
									<CartesianGrid strokeDasharray="3 3" />
									<Bar dataKey="sales" fill="#bbdefb" />
									<Area
										type="monotone"
										dataKey="sales"
										fill="#fce4ec"
										stroke="#e91e63"
									/>
								</ComposedChart>
							</ResponsiveContainer>
						</StyledCard>

						<StyledCard title="Sales on Area Chart" sizes={{ md: 4, sm: 6, xs: 12 }}>
							<Areachart
								data={salesData}
								xAxisKey="name"
								formatter="million units sold"
								areaData={{ key: 'sales' }}
							/>
						</StyledCard>

						<StyledCard title="Sales on Line Chart" sizes={{ md: 4, sm: 6, xs: 12 }}>
							<Linechart
								data={salesData}
								xAxisKey="name"
								formatter="million units sold"
								lineData={{ key: 'sales' }}
							/>
						</StyledCard>

						<StyledCard title="Sales on Pie Chart" sizes={{ md: 4, sm: 6, xs: 12 }}>
							<Piechart
								data={salesData}
								dataKey="sales"
								formatter="million units sold"
							/>
						</StyledCard>

						<StyledCard title="Sales on RadarChart" sizes={{ md: 4, sm: 6, xs: 12 }}>
							<Radarchart
								domain={[0, game.Global_Sales]}
								radarData={{ key: 'sales', name: 'Sales' }}
								axisKey="name"
								formatter="million units sold"
								data={salesData}
							/>
						</StyledCard>

						<StyledCard title="Scores on Bar Chart" sizes={{ md: 4, sm: 6, xs: 12 }}>
							<Barchart
								data={[
									{ name: 'Critic Score', score: game.Critic_Score },
									{ name: 'User Score', score: game.User_Score * 10 },
								]}
								xAxisKey="name"
								formatter="million units sold"
								barData={{ key: 'score', stroke: '#2196f3', fill: '#e3f2fd' }}
							/>
						</StyledCard>
					</Grid>
				) : (
					<div className={classes.notFound}>
						<Typography>Game Not Found!</Typography>
						<Button
							size="large"
							className="back-button"
							onClick={() => history.push('/')}
						>
							Go back
						</Button>
					</div>
				)}
			</React.Fragment>
		);
	}
}

export default withStyles(styles)(Game);
